// Client-side twin of the dimension check in lens-bundle's icon rendering (LOGO_MAX_DIMENSION in
// https://github.sc-corp.net/Snapchat/lens-bundle/blob/master/lens-bundle-service/src/main/java/com/snapchat/camplat/service/lens/bundle/service/weblens/WebLensConstants.java),
// feeding the icon dimension pre-check. PNG stores the pixel size in the IHDR chunk, which the format requires to
// be the FIRST chunk — so the measurement is a single 24-byte read of the file head, no decode.
//
// Guidance only, same philosophy as the zip measurement: anything unexpected (not a PNG
// signature, truncated head) yields null and the gate stays open — the icon type row already
// flags non-PNG picks, and the server-side conversion is the verdict on anything we can't read.

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const IHDR_TYPE = 0x49_48_44_52;
// Signature (8) + IHDR length (4) + IHDR type (4) + width (4) + height (4).
const HEAD_BYTES = 24;

export interface WebLensLogoMeasurement {
    widthPx: number;
    heightPx: number;
}

/** Reads the PNG's pixel dimensions from its IHDR chunk. Null for anything that is not a PNG. */
export const measureWebLensLogo = async (logo: Blob): Promise<WebLensLogoMeasurement | null> => {
    if (logo.size < HEAD_BYTES) {
        return null;
    }
    // A file that becomes unreadable between pick and read (moved, permissions) rejects here —
    // that is an unreadable icon, so it takes the same open-gate null as any other unreadable
    // input (mirrors measureWebLensBuildOutput's guard; the caller never handles rejections).
    let buffer: ArrayBuffer;
    try {
        buffer = await logo.slice(0, HEAD_BYTES).arrayBuffer();
    } catch {
        return null;
    }
    const head = new DataView(buffer);
    if (PNG_SIGNATURE.some((byte, index) => head.getUint8(index) !== byte)) {
        return null;
    }
    if (head.getUint32(12) !== IHDR_TYPE) {
        return null;
    }
    return { widthPx: head.getUint32(16), heightPx: head.getUint32(20) };
};
